import { State } from 'store';
import { TFunction } from 'react-i18next';
import { Ruleset, Rule } from 'entities/campaign';
import {
  Channel,
  EmailChannel,
  RTMInjectedChannel,
  RTMOverlayChannel,
  ScheduledChannel,
} from 'entities/campaign/rulesets/channels';
import { InactiveTime, Trigger } from 'entities/campaign/rulesets/rtm-trigger';
import { Selectors } from 'sections/campaigns/state/rulesets';
import { getRuleConfig } from './configs/ruleConfigs';
import { maximumDelay } from './configs/sendConfig';
import { hasBothEmailSourceRulesDefined } from '../utils/emailSourceRules';

export interface Progress {
  requiredFields: number;
  validFields: number;
  percent: number;
  messages: string[];
}

export const rulesetNameValidation = (name: string, translator: TFunction<'campaigns'>) => ({
  expression: name && name.length >= 5 && name.length <= 100,
  message: translator('progress.validation.requiredCharacterLength'),
});

export const getProgress = (
  state: State,
  rulesetId: string,
  sessionTimeout: number,
  translator: TFunction<'campaigns'>,
): Progress => {
  const ruleset = Selectors.getUpdatedEntity(state, rulesetId);
  return rulesetProgress(ruleset, sessionTimeout, translator);
};

export function rulesetProgress(
  ruleset: Ruleset,
  sessionTimeout: number,
  translator: TFunction<'campaigns'>,
): Progress {
  if (!ruleset) return toProgress(0, 0);
  const progressContext = { sessionTimeout };

  const progressItems = [
    validationProgress(rulesetNameValidation(ruleset.name, translator)),
    validationProgress({
      expression: Array.isArray(ruleset.channels) && ruleset.channels.length > 0,
      message: translator('progress.validation.requiresChannel'),
    }),
    reduceProgresses(ruleset.channels ?? [], progressContext, channelProgress, translator),
    reduceProgresses(ruleset.rules ?? [], progressContext, ruleProgress, translator),
  ];

  if (hasBothEmailSourceRulesDefined(ruleset.rules)) {
    progressItems.push(
      validationProgress({
        expression: false,
        message: translator('progress.validation.includesBothEmailSourceRules'),
      }),
    );
  }

  return add(progressItems);
}

function triggerProgress(triggers: Trigger, pc: ProgressContext, translator: TFunction<'campaigns'>): Progress {
  if (!triggers) return toProgress(0, 0);
  const triggerType = triggers.type ?? '';
  if (triggerType === '.InactiveTime') {
    const inactive = triggers as InactiveTime;
    return validationProgress({
      expression: inactive.time != null && inactive.time > 0,
      message: translator('progress.validation.inactiveTimeTrigger'),
    });
  }
  return toProgress(0, 0);
}

function ruleProgress(rule: Rule, pc: ProgressContext, translator: TFunction<'campaigns'>): Progress {
  if (!rule?.type) return toProgress(0, 0);
  const ruleType = rule.type;
  switch (ruleType) {
    case '.Geolocation': {
      const { valid, title } = getRuleConfig(translator, '.Geolocation');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noLocation', { title }),
      });
    }
    case '.DeviceType': {
      const { valid, title } = getRuleConfig(translator, '.DeviceType');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noDevice', { title }),
      });
    }
    case '.ActiveBasketContainsItemWithId': {
      const { valid, title } = getRuleConfig(translator, '.ActiveBasketContainsItemWithId');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noId', { title }),
      });
    }
    case '.ActiveBasketContainsItemWithName': {
      const { valid, title } = getRuleConfig(translator, '.ActiveBasketContainsItemWithName');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noName', { title }),
      });
    }
    case '.ActiveBasketValueIsAtLeast': {
      const { valid, title } = getRuleConfig(translator, '.ActiveBasketValueIsAtLeast');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noValue', { title }),
      });
    }
    case '.ActiveBasketValueIsLessThan': {
      const { valid, title } = getRuleConfig(translator, '.ActiveBasketContainsItemWithName');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.noLessThanValue', { title }),
      });
    }
    case '.BrowseAbandonment': {
      const { valid, title } = getRuleConfig(translator, '.BrowseAbandonment');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.positiveMinSessionTime', { title }),
      });
    }
    case '.CartAbandonment': {
      const { valid, title } = getRuleConfig(translator, '.CartAbandonment');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.defineUnsubscribe', { title }),
      });
    }
    case '.ExcludeEmailSource': {
      const { valid, title } = getRuleConfig(translator, '.ExcludeEmailSource');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.requiresExcludedEmailSources', { title }),
      });
    }
    case '.IncludeEmailSource': {
      const { valid, title } = getRuleConfig(translator, '.IncludeEmailSource');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.requiresIncludedEmailSources', { title }),
      });
    }
    case '.ImpressionPathWhitelist': {
      const { valid, title } = getRuleConfig(translator, '.ImpressionPathWhitelist');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.requiresWhitelist', { title }),
      });
    }
    case '.NoActiveMarketingPermission': {
      const { valid } = getRuleConfig(translator, '.NoActiveMarketingPermission');
      return validationProgress({
        expression: valid(rule),
        message: null,
      });
    }
    case '.NoActiveSMSMarketingPermission': {
      const { valid } = getRuleConfig(translator, '.NoActiveSMSMarketingPermission');
      return validationProgress({
        expression: valid(rule),
        message: null,
      });
    }
    case '.UserLoggedIn': {
      const { valid, title } = getRuleConfig(translator, '.UserLoggedIn');
      return validationProgress({
        expression: valid(rule),
        message: translator('progress.validation.rule.requiresLoginStatus', { title }),
      });
    }
    default:
      return toProgress(0, 0);
  }
}

function channelProgress(channel: Channel, pc: ProgressContext, translator: TFunction<'campaigns'>): Progress {
  if (!channel) return toProgress(0, 0);
  const type = channel.type ?? '';
  switch (type) {
    case '.Email': {
      const emailCh = channel as EmailChannel;
      return validationProgress(
        {
          expression: emailCh.templateId,
          message: translator('progress.validation.email.noTemplate', { channelType: channel.type }),
        },
        {
          expression: emailCh.delay >= pc.sessionTimeout,
          message: translator('progress.validation.email.validSessionTimeout', { channelType: channel.type }),
        },
        {
          expression: emailCh.delay <= maximumDelay,
          message: translator('progress.validation.email.validSendTime', { channelType: channel.type }),
        },
        {
          expression: emailCh.subjectLine,
          message: translator('progress.validation.email.validSubject', { channelType: channel.type }),
        },
      );
    }
    case '.RTMInjectedChannel': {
      const rtmInjected = channel as RTMInjectedChannel;
      return add([
        reduceProgresses(rtmInjected.rtmTrigger, pc, triggerProgress, translator),
        validationProgress(
          {
            expression: rtmInjected.templateId,
            message: translator('progress.validation.rtm.noTemplate', { channelType: channel.type }),
          },
          {
            expression: rtmInjected.selector,
            message: translator('progress.validation.rtm.noSelector', { channelType: channel.type }),
          },
          {
            expression: rtmInjected.templateInjectedPosition,
            message: translator('progress.validation.rtm.noPosition', { channelType: channel.type }),
          },
        ),
      ]);
    }

    case '.ScheduledChannel': {
      const scheduled = channel as ScheduledChannel;
      return add([
        validationProgress({
          expression: scheduled.duration,
          message: translator('progress.validation.scheduledChannel.noDuration', { channelType: channel.type }),
        }),
        channelProgress(scheduled.channel, pc, translator),
      ]);
    }

    case '.RTMOverlayChannel': {
      const rtmOverlay = channel as RTMOverlayChannel;
      return add([
        reduceProgresses(rtmOverlay.rtmTrigger, pc, triggerProgress, translator),
        validationProgress({
          expression: rtmOverlay.templateId,
          message: translator('progress.validation.rtmOverlay.noTemplate', { channelType: channel.type }),
        }),
      ]);
    }
    default:
      return toProgress(0, 0);
  }
}

function toProgress(validFields: number, requiredFields: number, messages: string[] = []): Progress {
  return {
    percent: requiredFields === 0 ? 100 : (100 * validFields) / requiredFields,
    validFields,
    requiredFields,
    messages,
  };
}

type ProgressContext = {
  sessionTimeout: number;
};

function reduceProgresses<T>(
  ts: T[] | undefined,
  pc: ProgressContext,
  f: (t: T, pc: ProgressContext, trns: TFunction<'campaigns'>) => Progress,
  translator: TFunction<'campaigns'>,
): Progress {
  if (!Array.isArray(ts)) return toProgress(0, 0);
  return ts.reduce((acc, next) => add([acc, f(next, pc, translator)]), toProgress(0, 0));
}

function validationProgress(...conditions: { expression: any; message: string }[]): Progress {
  const requiredFields = conditions.length;
  const validFields = conditions.filter(x => x.expression).length;
  const invalidMessages = conditions.filter(x => !x.expression).map(x => x.message);
  return toProgress(validFields, requiredFields, invalidMessages);
}

function add(progress: Progress[]): Progress {
  const valid = progress.filter((p): p is Progress => p != null && typeof p.validFields === 'number');
  return toProgress(
    valid.reduce((acc, curr) => acc + curr.validFields, 0),
    valid.reduce((acc, curr) => acc + curr.requiredFields, 0),
    valid.reduce((acc, curr): string[] => acc.concat(curr.messages), []),
  );
}
